/**
 * Side Nav
 *
 * Tracks scroll position and updates the active sidenav link
 * based on which section is currently in view.
 *
 * Features a liquid blob indicator that stretches between
 * positions when the active section changes.
 */

const ACTIVE_CLASS = "is-active";
const DOT_SIZE = 8;
const ANIM_DURATION = 150; // ms

function easeInCubic(t) {
  return t * t * t;
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function initSideNav() {
  const nav = document.querySelector(".side-nav");

  if (!nav) {
    return;
  }

  const links = Array.from(nav.querySelectorAll(".side-nav-link"));

  if (!links.length) {
    return;
  }

  // Build a map of link → anchor target element.
  const entries = links
    .map((link) => {
      const hash = link.getAttribute("href");
      const target = hash
        ? document.getElementById(hash.replace("#", ""))
        : null;
      return target ? { link, target } : null;
    })
    .filter(Boolean);

  if (!entries.length) {
    return;
  }

  // --- Liquid blob indicator ---

  const itemsContainer = nav.querySelector(".side-nav-items");
  const blob = document.createElement("span");
  blob.className = "side-nav-blob";
  blob.setAttribute("aria-hidden", "true");
  itemsContainer.appendChild(blob);

  const indicators = new Map();
  links.forEach((link) => {
    const indicator = link.querySelector(".side-nav-indicator");
    if (indicator) {
      indicators.set(link, indicator);
    }
  });

  let blobAnimId = null;
  let currentBlobY = null;
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  function getBlobPosition(link) {
    const indicator = indicators.get(link);
    if (!indicator) {
      return null;
    }
    const containerRect = itemsContainer.getBoundingClientRect();
    const indicatorRect = indicator.getBoundingClientRect();
    return {
      x: indicatorRect.left - containerRect.left,
      y: indicatorRect.top - containerRect.top,
    };
  }

  function snapBlob(link) {
    if (blobAnimId) {
      cancelAnimationFrame(blobAnimId);
      blobAnimId = null;
    }
    const pos = getBlobPosition(link);
    if (!pos) {
      return;
    }
    blob.style.left = pos.x + "px";
    blob.style.top = pos.y + "px";
    blob.style.height = DOT_SIZE + "px";
    currentBlobY = pos.y;
  }

  function animateBlob(link) {
    const pos = getBlobPosition(link);
    if (!pos) {
      return;
    }

    // Always sync horizontal position.
    blob.style.left = pos.x + "px";

    // Snap without animation on first paint or reduced motion.
    if (currentBlobY === null || prefersReducedMotion.matches) {
      snapBlob(link);
      return;
    }

    const fromY = currentBlobY;
    const toY = pos.y;

    if (fromY === toY) {
      return;
    }

    if (blobAnimId) {
      cancelAnimationFrame(blobAnimId);
      blobAnimId = null;
    }

    // Record target so any subsequent call knows our destination.
    currentBlobY = toY;

    const movingDown = toY > fromY;
    const startTime = performance.now();

    function tick(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / ANIM_DURATION, 1);

      // Leading edge arrives quickly, trailing edge catches up.
      const leading = easeOutCubic(t);
      const trailing = easeInCubic(t);

      let top;
      let bottom;

      if (movingDown) {
        top = fromY + (toY - fromY) * trailing;
        bottom = fromY + DOT_SIZE + (toY - fromY) * leading;
      } else {
        top = fromY + (toY - fromY) * leading;
        bottom = fromY + DOT_SIZE + (toY - fromY) * trailing;
      }

      const height = Math.max(DOT_SIZE, bottom - top);
      const stretch = (height - DOT_SIZE) / Math.abs(toY - fromY);

      blob.style.top = top + "px";
      blob.style.height = height + "px";
      blob.style.width = DOT_SIZE * (1 - stretch * 0.35) + "px";
      blob.style.opacity = 1 - stretch * 0.4;

      if (t < 1) {
        blobAnimId = requestAnimationFrame(tick);
      } else {
        blobAnimId = null;
      }
    }

    blobAnimId = requestAnimationFrame(tick);
  }

  // --- Scroll tracking ---

  let ticking = false;

  // Read the header height from the CSS custom property.
  const headerHeight =
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--header-height",
      ),
      10,
    ) || 122;

  function updateActive() {
    let current = entries[0];

    for (const entry of entries) {
      const top = entry.target.getBoundingClientRect().top;
      if (top <= headerHeight + 30) {
        current = entry;
      }
    }

    const prevActive = nav.querySelector("." + ACTIVE_CLASS);

    links.forEach((link) => link.classList.remove(ACTIVE_CLASS));
    current.link.classList.add(ACTIVE_CLASS);

    if (prevActive !== current.link) {
      animateBlob(current.link);
    }

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateActive);
      ticking = true;
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });

  // Smooth scroll on click.
  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      const hash = link.getAttribute("href");
      const target = hash
        ? document.getElementById(hash.replace("#", ""))
        : null;

      if (target) {
        e.preventDefault();
        const y = target.getBoundingClientRect().top + window.scrollY - 30;
        window.scrollTo({ top: y, behavior: "smooth" });
        history.replaceState(null, "", hash);
      }
    });
  });

  // Recalculate blob position on resize.
  window.addEventListener("resize", () => {
    const active = nav.querySelector("." + ACTIVE_CLASS);
    if (active) {
      snapBlob(active);
    }
  });

  // Set initial active state.
  updateActive();
  const initialActive = nav.querySelector("." + ACTIVE_CLASS);
  if (initialActive) {
    snapBlob(initialActive);
  }

  // Re-check after the browser finishes scrolling to a hash target,
  // since DOMContentLoaded fires before the hash scroll happens.
  if (window.location.hash) {
    window.addEventListener(
      "load",
      () => {
        requestAnimationFrame(updateActive);
      },
      { once: true },
    );
  }
}

document.addEventListener("DOMContentLoaded", initSideNav);
